import Vue from 'vue'
import Vuex from 'vuex'
import { getImageUrl } from '@/utils/imageUrl'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    // 用户状态
    user: {
      id: null,
      username: '',
      nickname: '',
      mobile: '',
      phone: '',
      avatar: '',
      email: '',
      score: 0,
      createtime: null,
      expiretime: null,
      expires_in: null,
      isLoggedIn: false
    },
    // 验证token
    token: null,
    // 登录弹框控制
    showLoginModal: false
  },
  getters: {
    // 获取用户信息
    getUser: state => state.user,
    // 获取登录状态
    isLoggedIn: state => state.user.isLoggedIn,
    // 获取token
    getToken: state => state.token,
    // 获取用户头像
    getUserAvatar: state => getImageUrl(state.user.avatar),
    // 获取用户名
    getUserName: state => state.user.nickname || state.user.name || '用户'
  },
  mutations: {
    // 设置用户信息
    SET_USER(state, userInfo) {
      state.user = {
        ...state.user,
        ...userInfo,
        isLoggedIn: true
      }
    },
    // 设置token
    SET_TOKEN(state, token) {
      state.token = token
    },
    // 清除用户信息
    CLEAR_USER(state) {
      state.user = {
        id: null,
        username: '',
        nickname: '',
        mobile: '',
        phone: '',
        avatar: '',
        email: '',
        score: 0,
        createtime: null,
        expiretime: null,
        expires_in: null,
        isLoggedIn: false
      }
      state.token = null
    },
    // 从localStorage或sessionStorage恢复用户状态
    RESTORE_USER_STATE(state, { user, token }) {
      state.user = {
        ...user,
        isLoggedIn: true // 确保恢复时设置登录状态
      }
      state.token = token
    },
    // 显示登录弹框
    SHOW_LOGIN_MODAL(state) {
      state.showLoginModal = true
    },
    // 隐藏登录弹框
    HIDE_LOGIN_MODAL(state) {
      state.showLoginModal = false
    }
  },
  actions: {
    // 登录操作
    async login({ commit }, { phone, password, remember }) {
      try {
        // 基本输入验证
        if (!phone || !password) {
          return { success: false, message: '请输入手机号和密码' }
        }
        
        // 手机号格式验证
        const phoneRegex = /^1[3-9]\d{9}$/
        if (!phoneRegex.test(phone)) {
          return { success: false, message: '请输入正确的手机号格式' }
        }
        
        // 密码长度验证
        if (password.length < 6) {
          return { success: false, message: '密码长度不能少于6位' }
        }
        
        // 调用真实的登录API
        const { login } = await import('@/api/index')
        const response = await login({
          account: phone,
          password: password
        })        
        
        // 提取返回的用户信息和token
        // API返回结构: { data: { userinfo: { ...用户信息, token: '...' } } }
        const userInfo = response.data?.userinfo || response.userinfo || response.user || response
        const token = userInfo.token || response.token || response.access_token
        
        // 标准化用户信息字段
        const normalizedUserInfo = {
          id: userInfo.id || userInfo.user_id,
          username: userInfo.username,
          nickname: userInfo.nickname,
          mobile: userInfo.mobile,
          phone: userInfo.phone || userInfo.mobile,
          avatar: userInfo.avatar,
          email: userInfo.email,
          score: userInfo.score,
          createtime: userInfo.createtime,
          expiretime: userInfo.expiretime,
          expires_in: userInfo.expires_in
        }
        
        // 提交用户信息和token
        commit('SET_USER', normalizedUserInfo)
        commit('SET_TOKEN', token)
        
        // 存储到localStorage或sessionStorage
        const storageData = {
          user: normalizedUserInfo,
          token: token,
          remember: remember,
          timestamp: Date.now()
        }
        
        if (remember) {
          localStorage.setItem('user_auth', JSON.stringify(storageData))
        } else {
          sessionStorage.setItem('user_auth', JSON.stringify(storageData))
        }
        
        return { success: true, message: '登录成功' }
        
      } catch (error) {
        console.error('登录错误:', error)
        return { success: false, message: error.msg || error.message || '网络错误，请稍后重试' }
      }
    },

    // 微信一键登录
    async wechatLogin({ commit }, { code, remember = true }) {
      try {
        // 调用微信登录API
        const { wechatLogin } = await import('@/api/index')
        const response = await wechatLogin({ code })
        
        // 提取返回的用户信息和token
        const userInfo = response.data?.userinfo || response.userinfo || response.user || response
        const token = userInfo.token || response.token || response.access_token
        
        
        // 标准化用户信息字段
        const normalizedUserInfo = {
          id: userInfo.id || userInfo.user_id,
          username: userInfo.username,
          nickname: userInfo.nickname,
          mobile: userInfo.mobile,
          phone: userInfo.phone || userInfo.mobile,
          avatar: userInfo.avatar,
          email: userInfo.email,
          score: userInfo.score,
          createtime: userInfo.createtime,
          expiretime: userInfo.expiretime,
          expires_in: userInfo.expires_in
        }
        
        // 提交用户信息和token
        commit('SET_USER', normalizedUserInfo)
        commit('SET_TOKEN', token)
        
        // 存储到localStorage
        const storageData = {
          user: normalizedUserInfo,
          token: token,
          remember: remember,
          timestamp: Date.now()
        }
        
        if (remember) {
          localStorage.setItem('user_auth', JSON.stringify(storageData))
        } else {
          sessionStorage.setItem('user_auth', JSON.stringify(storageData))
        }
        
        return { success: true, message: '登录成功' }
        
      } catch (error) {
        console.error('微信登录错误:', error)
        return { success: false, message: error.msg || error.message || '微信登录失败，请稍后重试' }
      }
    },
    
    // 退出登录
    logout({ commit }) {
      commit('CLEAR_USER')
      localStorage.removeItem('user_auth')
      sessionStorage.removeItem('user_auth')
    },
    
    // 初始化用户状态，从localStorage或sessionStorage恢复
    initUserState({ commit }) {
      // 优先从localStorage恢复
      let authData = localStorage.getItem('user_auth')
      if (!authData) {
        authData = sessionStorage.getItem('user_auth')
      }
      
      if (authData) {
        try {
          const parsed = JSON.parse(authData)
          
          // 检查token是否过期（可选：7天过期）
          const now = Date.now()
          const tokenAge = now - (parsed.timestamp || 0)
          const maxAge = 7 * 24 * 60 * 60 * 1000 // 7天
          
          if (tokenAge > maxAge) {
            // token过期，清除数据
            localStorage.removeItem('user_auth')
            sessionStorage.removeItem('user_auth')
            return
          }
          
          commit('RESTORE_USER_STATE', parsed)
        } catch (error) {
          console.error('恢复用户状态失败:', error)
          // 清除无效数据
          localStorage.removeItem('user_auth')
          sessionStorage.removeItem('user_auth')
        }
      }
    },

    // 更新用户资料
    async updateUserProfile({ commit, state }, profileData) {
      try {
        // 调用真实的更新用户信息API
        const { updateUserInfo } = await import('@/api/index')
        const response = await updateUserInfo(profileData)
        
        // 更新用户信息
        const updatedUser = {
          ...state.user,
          ...response
        }
        
        commit('SET_USER', updatedUser)
        
        // 更新存储的用户信息
        const authData = localStorage.getItem('user_auth') || sessionStorage.getItem('user_auth')
        if (authData) {
          const parsed = JSON.parse(authData)
          const updatedAuthData = {
            ...parsed,
            user: updatedUser,
            timestamp: Date.now()
          }
          
          // 根据原来的存储位置更新
          if (localStorage.getItem('user_auth')) {
            localStorage.setItem('user_auth', JSON.stringify(updatedAuthData))
          } else {
            sessionStorage.setItem('user_auth', JSON.stringify(updatedAuthData))
          }
        }
        
        return Promise.resolve(updatedUser)
      } catch (error) {
        console.error('更新用户资料失败:', error)
        return Promise.reject(error)
      }
    },

    // 上传用户头像
    async uploadUserAvatar({ commit, state }, formData) {
      try {
        // 调用上传文件API
        const { uploadFile, updateUserInfo } = await import('@/api/index')
        const uploadResponse = await uploadFile(formData)
        
        // 获取上传后的头像URL
        const avatarUrl = uploadResponse.data?.url || uploadResponse.url || uploadResponse.data?.file_url
        
        if (!avatarUrl) {
          throw new Error('上传失败，未获取到文件URL')
        }
        
        // 调用更新用户信息接口，将头像URL保存到后端
        await updateUserInfo({
          avatar: avatarUrl
        })
        
        // 更新用户头像
        const updatedUser = {
          ...state.user,
          avatar: avatarUrl
        }
        
        commit('SET_USER', updatedUser)
        
        // 更新存储的用户信息
        const authData = localStorage.getItem('user_auth') || sessionStorage.getItem('user_auth')
        if (authData) {
          const parsed = JSON.parse(authData)
          const updatedAuthData = {
            ...parsed,
            user: updatedUser,
            timestamp: Date.now()
          }
          
          if (localStorage.getItem('user_auth')) {
            localStorage.setItem('user_auth', JSON.stringify(updatedAuthData))
          } else {
            sessionStorage.setItem('user_auth', JSON.stringify(updatedAuthData))
          }
        }
        
        return Promise.resolve(avatarUrl)
      } catch (error) {
        console.error('上传头像失败:', error)
        return Promise.reject(error)
      }
    },

    // 修改用户密码
    async changeUserPassword({ commit, state }, { currentPassword, newPassword, confirmPassword }) {
      try {
        // 基本验证
        if (!currentPassword || !newPassword || !confirmPassword) {
          return Promise.reject(new Error('请填写所有密码字段'))
        }
        
        if (newPassword !== confirmPassword) {
          return Promise.reject(new Error('新密码与确认密码不一致'))
        }
        
        if (newPassword.length < 6) {
          return Promise.reject(new Error('新密码长度不能少于6位'))
        }
        
        // 调用真实的修改密码API
        const { changePassword } = await import('@/api/index')
        await changePassword({
          oldPassword: currentPassword,
          newPassword: newPassword
        })
        
        // 密码修改成功，这里可以选择是否需要重新登录
        // 在实际应用中，可能需要更新token或要求用户重新登录
        
        return Promise.resolve({ message: '密码修改成功' })
      } catch (error) {
        console.error('修改密码失败:', error)
        return Promise.reject(error)
      }
    }
  },
  modules: {
  }
})
